// pages/Contact.jsx
import React, { useState, useContext } from 'react';
import { 
  FaWhatsapp, 
  FaPhone, 
  FaEnvelope, 
  FaPaperPlane, 
  FaMapMarkerAlt, 
  FaClock, 
  FaCheckCircle,
  FaRobot
} from 'react-icons/fa';
import './Contact.css';
import { LanguageContext } from '../context/LanguageContext';

// رقم الواتساب المستهدف (بدون + أو 00)
const WHATSAPP_NUMBER = '201037334294';

const Contact = () => {
  const { language, toggleLanguage } = useContext(LanguageContext);

  const translations = {
    ar: {
      headerTitle: "تواصل معنا",
      headerDesc: "نسعد بتواصلك معنا! فريقنا جاهز للرد على استفساراتك",
      contactInfoTitle: "معلومات التواصل",
      workingHoursTitle: "ساعات العمل",
      workingHours: "السبت - الخميس: 9ص - 9م",
      formTitle: "أرسل لنا رسالة",
      formPlaceholders: {
        name: "الاسم الكامل",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        subject: "اختر الموضوع",
        message: "رسالتك..."
      },
      subjectOptions: {
        inquiry: "استفسار عام",
        service: "طلب خدمة",
        complaint: "شكوى أو اقتراح",
        support: "دعم فني"
      },
      submitBtn: "إرسال",
      submitting: "جاري الإرسال...",
      successMsg: "تم الإرسال بنجاح! سيتم التواصل معك عبر واتساب قريباً.",
      chatBotTitle: "شات بوت CodiCal الذكي",
      chatBotDesc: "اسألني أي حاجة عن خدماتنا الطبية أو خطط الدفع",
      chatInputPlaceholder: "اكتب سؤالك هنا...",
      chatSendBtn: "إرسال",
      contactInfo: [
        { icon: <FaPhone />, title: "اتصل بنا", info: "+201037334294", link: "tel:+201037334294" },
        { icon: <FaWhatsapp />, title: "واتساب", info: "+201037334294", link: "https://wa.me/201037334294" },
        { icon: <FaEnvelope />, title: "البريد الإلكتروني", info: "Support@codicalcompany.com", link: "mailto:Support@codicalcompany.com" },
        { icon: <FaMapMarkerAlt />, title: "موقعنا", info: "الغردقة، مصر", link: "#" }
      ]
    },
    en: {
      headerTitle: "Contact Us",
      headerDesc: "We are happy to hear from you! Our team is ready to answer your inquiries",
      contactInfoTitle: "Contact Information",
      workingHoursTitle: "Working Hours",
      workingHours: "Sat - Thu: 9AM - 9PM",
      formTitle: "Send Us a Message",
      formPlaceholders: {
        name: "Full Name",
        email: "Email",
        phone: "Phone",
        subject: "Select Subject",
        message: "Your Message..."
      },
      subjectOptions: {
        inquiry: "General Inquiry",
        service: "Service Request",
        complaint: "Complaint or Suggestion",
        support: "Technical Support"
      },
      submitBtn: "Send",
      submitting: "Sending...",
      successMsg: "Sent successfully! You will be contacted via WhatsApp shortly.",
      chatBotTitle: "CodiCal Smart Chatbot",
      chatBotDesc: "Ask me anything about our medical services or payment plans",
      chatInputPlaceholder: "Type your question here...",
      chatSendBtn: "Send",
      contactInfo: [
        { icon: <FaPhone />, title: "Call Us", info: "+201037334294", link: "tel:+201037334294" },
        { icon: <FaWhatsapp />, title: "WhatsApp", info: "+201037334294", link: "https://wa.me/201037334294" },
        { icon: <FaEnvelope />, title: "Email", info: "Support@codicalcompany.com", link: "mailto:Support@codicalcompany.com" },
        { icon: <FaMapMarkerAlt />, title: "Location", info: "Hurghada, Egypt", link: "#" }
      ]
    }
  };

  const current = translations[language];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // إرسال البيانات إلى واتساب (فتح الرابط بنص جاهز)
  const sendToWhatsApp = () => {
    // تحويل subject إلى نص مقروء
    let subjectText = '';
    switch (formData.subject) {
      case 'inquiry': subjectText = language === 'ar' ? 'استفسار عام' : 'General Inquiry'; break;
      case 'service': subjectText = language === 'ar' ? 'طلب خدمة' : 'Service Request'; break;
      case 'complaint': subjectText = language === 'ar' ? 'شكوى أو اقتراح' : 'Complaint or Suggestion'; break;
      case 'support': subjectText = language === 'ar' ? 'دعم فني' : 'Technical Support'; break;
      default: subjectText = formData.subject;
    }

    // تنسيق الرسالة بشكل جميل
    const messageText = language === 'ar' 
      ? `📋 *بيانات عميل جديد من موقع CodiCal* 📋\n\n👤 الاسم: ${formData.name}\n📧 البريد الإلكتروني: ${formData.email}\n📞 رقم الهاتف: ${formData.phone}\n🏷️ الموضوع: ${subjectText}\n💬 الرسالة: ${formData.message}`
      : `📋 *New Customer Data from CodiCal Website* 📋\n\n👤 Name: ${formData.name}\n📧 Email: ${formData.email}\n📞 Phone: ${formData.phone}\n🏷️ Subject: ${subjectText}\n💬 Message: ${formData.message}`;

    const encodedMessage = encodeURIComponent(messageText);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    
    // فتح واتساب في نافذة جديدة
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    setSubmitSuccess(false);

    try {
      // إرسال البيانات إلى واتساب
      sendToWhatsApp();
      
      setSubmitSuccess(true);
      // إعادة تعيين الفورم
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error:', error);
      setSubmitError(true);
      setTimeout(() => setSubmitError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const userText = chatMessage;
    setChatMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: userText }]);
    setIsChatLoading(true);

    try {
      const storedUser = localStorage.getItem("userData") || sessionStorage.getItem("userData");
      let sessionName = "زائر";
      if(storedUser) {
        try { const u = JSON.parse(storedUser); if(u.full_name) sessionName = u.full_name; } catch(e) {}
      }

      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/gemini_chat.php' : '/api/gemini_chat.php';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText, history: chatHistory, session_name: sessionName })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setChatHistory(prev => [...prev, { role: 'bot', content: data.reply }]);
      } else {
        setChatHistory(prev => [...prev, { role: 'bot', content: data.message || "حدث خطأ في النظام" }]);
      }
    } catch (err) {
      setChatHistory(prev => [...prev, { role: 'bot', content: "عذراً، الخادم مسؤل الذكاء الاصطناعي لا يستجيب." }]);
    }
    setIsChatLoading(false);
  };
  // =========================================================================

  return (
    <div className="contact-page" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* lang toggle removed */}

      {/* Fixed WhatsApp Button */}
      <a 
        href="https://wa.me/201037334294?text=السلام%20عليكم%20أحتاج%20استشارة"
        className="whatsapp-fixed"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaWhatsapp />
      </a>

      <div className="contact-container">
        {/* Header */}
        <div className="contact-header">
          <h1>{current.headerTitle}</h1>
          <p>{current.headerDesc}</p>
        </div>

        {/* Contact Grid */}
        <div className="contact-grid">
          {/* Contact Info Section */}
          <div className="contact-info-section">
            <h2 className="section-title">{current.contactInfoTitle}</h2>
            <div className="info-cards">
              {current.contactInfo.map((item, index) => (
                <a key={index} href={item.link} className="info-card" target={item.link.startsWith('http') ? "_blank" : "_self"} rel="noopener noreferrer">
                  <div className="info-icon">{item.icon}</div>
                  <div className="info-content">
                    <h3>{item.title}</h3>
                    <p>{item.info}</p>
                  </div>
                </a>
              ))}
            </div>
            {/* Working Hours */}
            <div className="info-card info-card-hours">
              <div className="info-icon"><FaClock /></div>
              <div className="info-content">
                <h3>{current.workingHoursTitle}</h3>
                <p>{current.workingHours}</p>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-form-section">
            <h2 className="section-title">{current.formTitle}</h2>
            {submitSuccess && (
              <div className="success-message">
                <FaCheckCircle />
                <span>{current.successMsg}</span>
              </div>
            )}
            {submitError && (
              <div className="error-message">
                <span>❌ {language === 'ar' ? 'فشل الإرسال. حاول مرة أخرى أو تواصل عبر واتساب مباشرة' : 'Failed to send. Please try again or contact us directly on WhatsApp.'}</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input type="text" name="name" placeholder={current.formPlaceholders.name} value={formData.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder={current.formPlaceholders.email} value={formData.email} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <input type="tel" name="phone" placeholder={current.formPlaceholders.phone} value={formData.phone} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <select name="subject" value={formData.subject} onChange={handleChange} required>
                  <option value="">{current.formPlaceholders.subject}</option>
                  <option value="inquiry">{current.subjectOptions.inquiry}</option>
                  <option value="service">{current.subjectOptions.service}</option>
                  <option value="complaint">{current.subjectOptions.complaint}</option>
                  <option value="support">{current.subjectOptions.support}</option>
                </select>
              </div>
              <div className="form-group">
                <textarea name="message" placeholder={current.formPlaceholders.message} value={formData.message} onChange={handleChange} required></textarea>
              </div>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? current.submitting : <>{current.submitBtn} <FaPaperPlane /></>}
              </button>
            </form>
          </div>
        </div>

        {/* Chatbot Section */}
        <div className="chatbot-section">
          <div className="chatbot-card">
            <div className="chatbot-header">
              <FaRobot className="chatbot-icon" />
              <h3>{current.chatBotTitle}</h3>
            </div>
            <p>{current.chatBotDesc}</p>

            <div className="chat-messages" style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '15px', padding: '10px', background: '#f5f7fa', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
              {chatHistory.length === 0 && <p style={{ textAlign: 'center', color: '#888', marginTop: '20px' }}>ابحث أو اسأل هنا...</p>}
              {chatHistory.map((msg, idx) => (
                <div key={idx} style={{ marginBottom: '10px', padding: '12px 15px', borderRadius: '12px', background: msg.role === 'user' ? '#0E74AB' : '#fff', color: msg.role === 'user' ? '#fff' : '#333', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', borderBottomRightRadius: msg.role === 'user' ? '0' : '12px', borderBottomLeftRadius: msg.role === 'bot' ? '0' : '12px' }}>
                  <strong style={{ fontSize: '13px', opacity: 0.8, marginBottom: '5px', display: 'block' }}>{msg.role === 'user' ? 'أنت' : '🤖 CodiCal AI'}</strong>
                  <span style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>{msg.content}</span>
                </div>
              ))}
              {isChatLoading && <div style={{ color: '#888', textAlign: 'center', margin: '10px 0' }}>جاري الكتابة... ⏳</div>}
            </div>

            <form onSubmit={handleChatSubmit} className="chat-form">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder={current.chatInputPlaceholder}
                className="chat-input"
              />
              <button type="submit" className="chat-send">
                <FaPaperPlane /> {current.chatSendBtn}
              </button>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section">
          <div className="map-placeholder">
            <p>📍 Hurghada, Egypt - CodiCal HQ</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;